/**
 * Mega-menu opening behaviour.
 *
 * Dawn's menus are <details> elements: click to open, Esc and focus-out to
 * close, and full keyboard operation already. All of that is left alone. This
 * adds hovering on top of it, for an audience that does not necessarily know a
 * menu is clickable.
 *
 * The rules that matter, and why:
 *
 * - Opening waits 200ms. A pointer crossing "eTrikes" on its way to "Support"
 *   should not fire a full-width panel at someone.
 * - Nothing closes on mouse-out. A panel that vanishes when the pointer strays
 *   a few pixels off the path between the trigger and the thing being aimed at
 *   is the single most common way these menus fail an unsteady hand. It closes
 *   when a choice is made, when another menu opens, on Esc, or on a click
 *   outside — never because the pointer wandered.
 * - Hover only where there is a real pointer. On a touch screen a hover state
 *   is a tap that did not navigate.
 */
(function () {
  const HOVER_INTENT_MS = 200;

  const fine =
    window.matchMedia &&
    window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  const header = document.querySelector('.header__inline-menu');
  if (!header) return;

  const menus = Array.from(header.querySelectorAll('header-menu > details'));
  if (!menus.length) return;

  let openTimer = null;

  function summaryOf(details) {
    return details.querySelector('summary');
  }

  /* Dawn conveys state through the details element alone. aria-expanded is set
     here as well, and kept in step, because assistive tech in the wild reads
     the summary rather than inferring from the parent. */
  function sync(details) {
    const summary = summaryOf(details);
    if (summary) summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');
  }

  function close(details) {
    if (!details.open) return;
    details.open = false;
    sync(details);
  }

  function closeOthers(except) {
    menus.forEach((d) => {
      if (d !== except) close(d);
    });
  }

  function open(details) {
    closeOthers(details);
    if (!details.open) {
      details.open = true;
      sync(details);
    }
  }

  menus.forEach((details) => {
    sync(details);

    /* Click is Dawn's own behaviour — the <details> toggles itself, so this
       only mirrors the state and makes sure the others shut. */
    details.addEventListener('toggle', () => {
      sync(details);
      if (details.open) closeOthers(details);
    });

    /* Choosing something closes the menu it was chosen from. Without this the
       panel is still hanging over the page when the next one loads. */
    details.addEventListener('click', (event) => {
      if (event.target.closest('a')) close(details);
    });

    if (!fine) return;

    details.addEventListener('pointerenter', (event) => {
      if (event.pointerType !== 'mouse') return;
      clearTimeout(openTimer);
      openTimer = setTimeout(() => open(details), HOVER_INTENT_MS);
    });

    /* Leaving cancels a pending open. It does not close an open panel. */
    details.addEventListener('pointerleave', (event) => {
      if (event.pointerType !== 'mouse') return;
      clearTimeout(openTimer);
    });
  });

  /* A panel opened by hover holds no focus, so Dawn's focusout never fires and
     Esc never reaches it. Both are handled at the document instead. */
  document.addEventListener('pointerdown', (event) => {
    if (event.target.closest('.header__inline-menu')) return;
    clearTimeout(openTimer);
    menus.forEach(close);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const openMenu = menus.find((d) => d.open);
    if (!openMenu) return;
    clearTimeout(openTimer);
    close(openMenu);
    const summary = summaryOf(openMenu);
    if (summary) summary.focus();
  });
})();
